// ---------------------------------------------------------------------------
// Studio generation failure surface — the ONE place a failed create/generate
// request becomes something the user can actually see and act on.
//
// Before this module the composer's create path surfaced failures ONLY as
// toasts: `onGenerationError` fired `toast.error(<message>)` (auto-dismissed
// after 2.5s, and rendered inside a single-line `studio-ellipsis-flex` span, so
// a multi-sentence explanation was clipped to a few words), and the catch fired
// a second, contradictory generic 'Failed to create project'. Meanwhile the
// optimistic `pendingChatNotice` ("Creating your project…") was left in place
// and auto-dismissed itself 8s later. Net effect for a typed fail-closed such
// as `StudioSourceBoundGenerationUnavailableError`: the request died, the
// reason existed and was correct, and ~10 seconds later the screen carried no
// trace of it at all — exactly the "silently fails" report.
//
// The durable surface is the chat thread itself: an `ai-result-failure` message
// survives `progressOnly` filtering in every chat panel, renders a localized
// humanized title + detail, and carries a recovery button. This module writes
// that message, replaces the stale progress notice with the same wording, and
// makes sure the chat panel is open so the bubble is on screen.
// ---------------------------------------------------------------------------

import { useStudioStore } from '@/store/studio-store'
import {
  humanizeAppGenerationFailureReason,
  type HumanizedFailure,
} from '@/shell/ide-v2/components/miss-reason-copy'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'

/**
 * Read the stable `reasonCode` a typed Studio failure carries.
 *
 * Typed failures (e.g. `StudioSourceBoundGenerationUnavailableError`) expose a
 * machine-stable code that the localized copy table keys on. Untyped errors
 * return `null` and fall back to their raw message, which the renderer shows to
 * developers only — creators get the generic localized headline instead of
 * engine prose.
 */
export function studioGenerationFailureReasonCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null
  const code = (error as { reasonCode?: unknown }).reasonCode
  return typeof code === 'string' && code.trim() ? code.trim() : null
}

export interface SurfacedStudioGenerationFailure {
  /** Machine-stable code when the error was typed, else its raw message. */
  reason: string
  /** Localized, creator-safe copy that was written to the chat thread. */
  humanized: HumanizedFailure
  /** Single-line wording mirrored into the pending chat notice. */
  notice: string
}

function noticeText(humanized: HumanizedFailure): string {
  return humanized.detail
    ? `${humanized.title} ${humanized.detail}`
    : humanized.title
}

/**
 * Make a failed app-generation request visible and durable.
 *
 * Idempotent per call site — callers invoke it exactly once, in the catch that
 * owns the request. Returns the resolved copy so the caller can reuse the SAME
 * wording for its toast fallback instead of contradicting the bubble.
 */
export function surfaceStudioGenerationFailure(input: {
  error: unknown
  /** The prompt that produced this failure — binds per-bubble recovery. */
  intent: string
  locale: LocaleCode
}): SurfacedStudioGenerationFailure {
  const reasonCode = studioGenerationFailureReasonCode(input.error)
  const rawMessage =
    input.error instanceof Error
      ? input.error.message
      : typeof input.error === 'string'
        ? input.error
        : ''
  const reason = reasonCode ?? (rawMessage || 'app-generation-workflow-failed')
  const humanized = humanizeAppGenerationFailureReason(reason, input.locale)
  const notice = noticeText(humanized)

  const store = useStudioStore.getState()
  store.appendChatMessage({
    kind: 'ai-result-failure',
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    reason,
    // Developer-persona-only at render time (see the ai-result-failure case in
    // chat-messages.tsx) — never shown to creators, so raw engine prose here is
    // safe and keeps the console-only detail on screen for debugging.
    ...(rawMessage ? { details: rawMessage } : {}),
    intent: input.intent,
    source: 'app-generation',
  })
  // Replace the optimistic "Creating your project…" line rather than letting it
  // sit there describing work that is no longer running.
  store.setPendingChatNotice(notice)
  // A collapsed chat panel would hide the only durable copy of this failure.
  store.setChatSidebarOpen(true)

  return { reason, humanized, notice }
}
